import React from 'react';
import { motion } from 'framer-motion';
import { Mic, FileText, Activity, Circle } from 'lucide-react';
import GrainOverlay from './GrainOverlay';

const ProductShowcase: React.FC = () => {
  return (
    <section className="py-20 bg-primary-50/50 dark:bg-dark-900/50 relative overflow-hidden">
      {/* ===== Grain Texture — fades in from top ===== */}
      <GrainOverlay fade="top" />

      {/* ===== Living Cells Background Decoration ===== */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {/* Main Cell - center-left area */}
        <motion.div
          className="absolute top-1/2 left-[8%] -translate-y-1/2 w-44 h-44 md:w-56 md:h-56 border-2 border-primary-400/50 dark:border-primary-600/20 bg-primary-200/25 dark:bg-primary-900/10"
          style={{ borderRadius: '40% 60% 55% 45% / 50% 45% 55% 50%' }}
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        >
          {/* Nucleus */}
          <motion.div
            className="absolute top-[30%] left-[35%] w-12 h-12 md:w-16 md:h-16 bg-primary-400/30 dark:bg-primary-400/12 rounded-full border border-primary-500/40 dark:border-primary-400/20"
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary-500/45 dark:bg-primary-500/20 rounded-full" />
          </motion.div>

          {/* Organelle dots */}
          <div className="absolute top-[18%] left-[65%] w-2 h-2 bg-primary-500/40 dark:bg-accent-400/15 rounded-full" />
          <motion.div
            className="absolute top-[60%] left-[20%] w-2 h-2 bg-primary-500/40 dark:bg-accent-400/15 rounded-full"
            animate={{ opacity: [0.25, 0.5, 0.25] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="absolute top-[72%] left-[55%] w-2 h-2 bg-primary-500/40 dark:bg-accent-400/15 rounded-full" />
          <motion.div
            className="absolute top-[45%] left-[75%] w-2 h-2 bg-primary-500/40 dark:bg-accent-400/15 rounded-full"
            animate={{ opacity: [0.25, 0.55, 0.25] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
          />
        </motion.div>

        {/* Daughter Cell 1 - upper-right */}
        <motion.div
          className="absolute top-[10%] right-[5%] w-24 h-24 md:w-28 md:h-28 border-2 border-primary-400/40 dark:border-primary-600/15 bg-primary-200/20 dark:bg-primary-900/8"
          style={{ borderRadius: '45% 55% 50% 50% / 55% 45% 55% 45%' }}
          animate={{ y: [0, -8, 0], x: [0, 3, 0], scale: [1, 1.02, 1] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        >
          <div className="absolute top-[32%] left-[38%] w-7 h-7 md:w-8 md:h-8 bg-primary-400/25 dark:bg-primary-400/10 rounded-full border border-primary-500/35 dark:border-primary-400/12">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-primary-500/40 dark:bg-primary-500/15 rounded-full" />
          </div>
          <div className="absolute top-[65%] left-[60%] w-1.5 h-1.5 bg-primary-500/35 dark:bg-accent-400/12 rounded-full" />
        </motion.div>

        {/* Daughter Cell 2 - bottom-right */}
        <motion.div
          className="absolute bottom-[12%] right-[10%] w-16 h-16 md:w-20 md:h-20 border-2 border-primary-400/35 dark:border-primary-600/12 bg-primary-200/15 dark:bg-primary-900/6"
          style={{ borderRadius: '50% 45% 55% 50% / 45% 55% 45% 55%' }}
          animate={{ y: [0, 6, 0], x: [0, -4, 0], scale: [1, 1.04, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        >
          <div className="absolute top-[30%] left-[35%] w-5 h-5 md:w-6 md:h-6 bg-primary-400/25 dark:bg-primary-400/10 rounded-full border border-primary-500/30 dark:border-primary-400/10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-primary-500/35 dark:bg-primary-500/12 rounded-full" />
          </div>
        </motion.div>

        {/* Membrane bridges */}
        <motion.div
          className="absolute top-[28%] left-[18%] md:left-[22%] w-14 md:w-16 h-[2px] bg-gradient-to-r from-primary-400/40 to-primary-400/15 dark:from-primary-600/15 dark:to-primary-600/5 origin-left"
          style={{ transform: 'rotate(-30deg)' }}
          animate={{ opacity: [0.3, 0.55, 0.3] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-[30%] left-[15%] md:left-[18%] w-10 md:w-12 h-[2px] bg-gradient-to-r from-primary-400/15 to-primary-400/40 dark:from-primary-600/5 dark:to-primary-600/15 origin-right"
          style={{ transform: 'rotate(25deg)' }}
          animate={{ opacity: [0.25, 0.5, 0.25] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
        />

        {/* Floating particles */}
        {[
          { pos: 'top-[15%] left-[15%]', anim: { y: [0, -6, 0], x: [0, 3, 0] }, dur: 6, del: 0 },
          { pos: 'top-[60%] right-[8%]', anim: { y: [0, 5, 0], x: [0, -3, 0] }, dur: 7, del: 2 },
          { pos: 'bottom-[20%] right-[30%]', anim: { y: [0, -4, 0], x: [0, 4, 0] }, dur: 8, del: 1 },
          { pos: 'top-[40%] left-[3%]', anim: { y: [0, 5, 0], x: [0, -2, 0] }, dur: 9, del: 3 },
          { pos: 'bottom-[35%] left-[25%]', anim: { y: [0, -5, 0], x: [0, 3, 0] }, dur: 7.5, del: 0.8 },
        ].map((p, i) => (
          <motion.div
            key={i}
            className={`absolute ${p.pos} w-2 h-2 bg-primary-500/40 dark:bg-primary-300/15 rounded-full`}
            animate={{ ...p.anim, opacity: [0.3, 0.55, 0.3] }}
            transition={{ duration: p.dur, repeat: Infinity, ease: 'easeInOut', delay: p.del }}
          />
        ))}
      </div>

      {/* ===== Foreground Content ===== */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative" style={{ zIndex: 1 }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark-900 dark:text-dark-100 mb-6">
              Experience the Future of Healthcare Management
            </h2>
            <p className="text-lg md:text-xl text-dark-500 dark:text-dark-300 mb-8 leading-relaxed">
              See how BioAxis transforms daily workflows with intelligent automation and intuitive design.
            </p>

            <div className="space-y-4">
              {[
                'Real-time collaboration across teams',
                'AI-powered insights and automation',
                'Seamless integration with existing systems'
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 * index }}
                >
                  <div className="w-2.5 h-2.5 bg-primary-500 rounded-full" />
                  <span className="text-dark-600 dark:text-dark-200">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Interface Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="glass-effect rounded-3xl overflow-hidden shadow-warm-lg">
              {/* Window Header */}
              <div className="flex items-center justify-between p-4 bg-primary-50/50 dark:bg-dark-800/50 border-b border-primary-200/30 dark:border-primary-900/20">
                <div className="flex gap-2">
                  <Circle className="w-3 h-3 text-primary-500 fill-current" />
                  <Circle className="w-3 h-3 text-secondary-500 fill-current" />
                  <Circle className="w-3 h-3 text-accent-500 fill-current" />
                </div>
                <span className="text-sm font-semibold text-dark-700 dark:text-dark-200">BioAxis NoteAxis</span>
              </div>

              {/* Window Content */}
              <div className="grid grid-cols-3 min-h-[300px]">
                {/* Left Sidebar */}
                <div className="p-4 border-r border-primary-200/20 dark:border-primary-900/15">
                  {/* Patient Info */}
                  <div className="glass-effect p-3 rounded-2xl mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full" />
                      <div>
                        <h4 className="text-dark-800 dark:text-dark-100 font-semibold text-sm">Sarah Johnson, 34</h4>
                        <p className="text-dark-400 dark:text-dark-400 text-xs">Routine Checkup</p>
                      </div>
                    </div>
                  </div>

                  {/* Navigation Sections */}
                  <div className="space-y-2">
                    {[
                      { icon: Mic, label: 'Voice Notes', active: true },
                      { icon: FileText, label: 'Medical History', active: false },
                      { icon: Activity, label: 'Analytics', active: false },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-2 p-2 rounded-xl transition-colors ${
                          item.active
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                            : 'text-dark-400 dark:text-dark-400 hover:text-dark-700 dark:hover:text-dark-200'
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="text-sm">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Main Content Area */}
                <div className="col-span-2 p-4">
                  <div className="glass-effect p-4 rounded-2xl h-full">
                    {/* Recording Header */}
                    <div className="flex items-center gap-2 mb-4 text-dark-500 dark:text-dark-300 text-sm">
                      <motion.div
                        className="w-2 h-2 bg-secondary-500 rounded-full"
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                      <span>Recording...</span>
                    </div>

                    {/* Transcription Text */}
                    <div className="text-dark-700 dark:text-dark-200 text-sm leading-relaxed">
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                      >
                        Patient reports feeling well overall. Blood pressure appears normal.
                        Recommended annual screening labs...
                      </motion.p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
